import { TableTotalsDialogPayload } from "../components/rules/tableTotals/tableTotalsResult.types";

/**
 * Same-origin localStorage key used to hand the snapshot payload to the details dialog.
 * The dialog reads and deletes this key on load (see API Contract §5).
 * Value MUST match the key consumed by the dialog app (T7) and documented in learnings.md.
 */
const DIALOG_PAYLOAD_KEY = "IDS_TABLE_TOTALS_DIALOG_PAYLOAD";

/** URL mirrors the LoginDialog.ts pattern: same origin, subpath matches the webpack HtmlWebpackPlugin output. */
const dialogUrl = (): string => window.location.origin + "/dialog/tableTotalsDetails.html";

/**
 * Module-scoped handle for the currently open dialog.
 * Non-null while the dialog window is alive; used by the double-open guard (AC-GAP-08).
 */
let activeDialog: Office.Dialog | null = null;

/** Clears the module-level handle and the localStorage payload key. */
function clearDialog(): void {
  activeDialog = null;
  localStorage.removeItem(DIALOG_PAYLOAD_KEY);
}

/**
 * Handles messages posted from the dialog via messageParent.
 * The only expected message is the literal string "close", which the dialog sends
 * when the user dismisses it.  On receipt the pane calls dialog.close() and clears state.
 */
function onMessage(arg: any): void {
  if (arg.message === "close") {
    if (activeDialog) {
      activeDialog.close();
    }
    clearDialog();
  }
}

/**
 * Handles DialogEventReceived — fired when the dialog is closed by the user (X button)
 * or dismissed by Office without going through messageParent.  State is cleared; the
 * dialog window is already gone so dialog.close() is not called.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function onDialogEvent(_arg: unknown): void {
  clearDialog();
}

/**
 * Opens the TABLE_TOTALS details dialog.
 *
 * Protocol (API Contract §5):
 *  1. If a dialog is already open, no-op (AC-GAP-08 double-open guard).
 *  2. Serialise `payload` to localStorage under DIALOG_PAYLOAD_KEY so the separate
 *     dialog window (different JS context) can read it on load.
 *  3. Open `tableTotalsDetails.html` via Office.context.ui.displayDialogAsync.
 *  4. Subscribe to DialogMessageReceived; when the dialog posts "close" → call
 *     dialog.close(), clear the handle, and remove the localStorage key.
 *  5. Subscribe to DialogEventReceived (user X) → clear handle + localStorage key.
 */
export const openTableTotalsDetailsDialog = (payload: TableTotalsDialogPayload): void => {
  // AC-GAP-08: prevent multiple concurrent dialogs
  if (activeDialog !== null) {
    return;
  }

  localStorage.setItem(DIALOG_PAYLOAD_KEY, JSON.stringify(payload));

  Office.context.ui.displayDialogAsync(
    dialogUrl(),
    { height: 60, width: 60, promptBeforeOpen: false }, //don't factorize me there's a bug preventing dialog to reopen
    (result) => {
      if (result.status === Office.AsyncResultStatus.Failed) {
        localStorage.removeItem(DIALOG_PAYLOAD_KEY);
        return;
      }
      activeDialog = result.value;
      activeDialog.addEventHandler(Office.EventType.DialogMessageReceived, onMessage);
      activeDialog.addEventHandler(Office.EventType.DialogEventReceived, onDialogEvent);
    }
  );
};
